/**
 * Helpers for TABLE / COLUMN_HEADER questions.
 *
 * A table's answer is a JSON array of row objects, keyed by column question id:
 *   [{ "34": "Chicago", "35": "12/11/2026" }, ...]
 *
 * Rows carry their row header under ROW_HEADER_KEY rather than relying on array
 * position, so pruning a blank row can never re-map a later row's header.
 */
export const ROW_HEADER_KEY = '__row_header';

/**
 * Key under which a TABLE_FILE row stores its uploaded file. The value is either
 * a freshly-picked file ({ name, type, size, data }) or, once saved, the stored
 * reference ({ key, name, type, size }). Must match the backend saveAnswers.
 */
export const TABLE_FILE_KEY = '__file';

/** Question types that render as a table (share column/row mechanics). */
export function isTableType(typeId: any): boolean {
  return typeId === 'TABLE' || typeId === 'TABLE_FILE';
}

/** COLUMN_HEADER questions belonging to a given TABLE, in the order given. */
export function tableColumns(questions: any[], tableId: any): any[] {
  const id = Number(tableId);
  return (questions || []).filter((q) =>
    q && q.type_id === 'COLUMN_HEADER' && !q.archived &&
    (q.table_question_ids || []).map(Number).includes(id));
}

/** True when a TABLE_FILE row carries a file (either freshly picked or saved). */
export function rowHasFile(row: any): boolean {
  const f = row && row[TABLE_FILE_KEY];
  return !!(f && (f.data || f.key));
}

/**
 * A row is blank when every column cell is empty. Only column ids are inspected:
 * a row header alone must not make a row non-blank, and the renderer also writes
 * `_other` / `_confirm` keys into the row that don't count as content.
 *
 * `false` counts as empty: the only boolean cell type is CHECKBOX, which Angular
 * leaves at false once ticked and unticked again. Treating that as content would
 * pin the row non-blank forever and fire its required rules. This matches how
 * EvaluationQuestion.saveAnswers already stores a false answer as null.
 */
export function isTableRowBlank(row: any, columns: any[]): boolean {
  if (!row) return true;
  // A TABLE_FILE row with an uploaded file is content regardless of its cells.
  if (rowHasFile(row)) return false;
  return (columns || []).every((c) => isBlankCell(row[c.id]));
}

function isBlankCell(v: any): boolean {
  return v === undefined || v === null || v === '' || v === false
    || (Array.isArray(v) && v.length === 0);
}

/**
 * Rows arrive from JSON, so DATE cells are ISO strings; bsDatepicker needs Dates.
 * Mirrors the same conversion the evaluation page does for standalone DATE answers.
 */
export function hydrateTableRows(rows: any[], columns: any[]): any[] {
  if (!Array.isArray(rows)) return [];
  const dateColumnIds = (columns || [])
    .filter((c) => c.column_type_id === 'DATE')
    .map((c) => c.id);
  if (!dateColumnIds.length) return rows;
  return rows.map((row) => {
    const hydrated = { ...row };
    dateColumnIds.forEach((id) => {
      if (typeof hydrated[id] === 'string' && hydrated[id]) {
        const d = new Date(hydrated[id]);
        if (!isNaN(d.getTime())) hydrated[id] = d;
      }
    });
    return hydrated;
  });
}

/**
 * `evaluation_questions` rows mapped to their question, and the subset that should
 * get a row of its own on the form (COLUMN_HEADERs render only inside their table).
 * Cached on the source array's identity so bindings keep a stable reference across
 * change detection; these lists are assigned once and never mutated in place.
 */
const listCache = new WeakMap<any[], { all: any[], visible: any[] }>();

function cachedLists(evaluationQuestions: any[]): { all: any[], visible: any[] } {
  if (!Array.isArray(evaluationQuestions)) return { all: [], visible: [] };
  let cached = listCache.get(evaluationQuestions);
  if (!cached) {
    cached = {
      all: evaluationQuestions.map((eq) => eq && eq.question).filter(Boolean),
      visible: evaluationQuestions.filter((eq) => eq && eq.question
        && eq.question.type_id !== 'COLUMN_HEADER'),
    };
    listCache.set(evaluationQuestions, cached);
  }
  return cached;
}

/** Every question on the form, in form order — pass as [siblingQuestions]. */
export function formQuestions(evaluationQuestions: any[]): any[] {
  return cachedLists(evaluationQuestions).all;
}

/** evaluation_questions rows to actually loop over, minus the column headers. */
export function visibleFormRows(evaluationQuestions: any[]): any[] {
  return cachedLists(evaluationQuestions).visible;
}

/**
 * Drop entirely blank rows from every TABLE answer. Call at submit time, never at
 * render time — a row must stay visible while it is being filled in.
 */
export function pruneBlankTableRows(questions: any[], answers: any): void {
  if (!answers) return;
  (questions || [])
    .filter((q) => q && isTableType(q.type_id))
    .forEach((table) => {
      const rows = answers[table.id];
      if (!Array.isArray(rows)) return;
      const columns = tableColumns(questions, table.id);
      answers[table.id] = rows.filter((row) => !isTableRowBlank(row, columns));
    });
}
